import { DialogComponent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { createLogger, createReporter } from "@theme/logger";

const log = createLogger("CartDrawer");
const reportError = createReporter("CartDrawer");

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    // A cart write can refresh the count without opening the drawer.
    // Callers set openDrawer: false (landing checkout leaves for Shopify).
    if (event.detail?.data?.openDrawer === false) return;

    if (this.hasAttribute("auto-open")) {
      this.open();
    }
  };

  /**
   * Mysa-only: after opening the drawer, fetch the current cart and publish a
   * `theme:cart_drawer_viewed` analytics event for GTM/GA4 pixels.
   * Upstream Horizon's `cart-drawer.js` does not fetch cart state in `open()`.
   *
   * The `/cart.js` fetch is hardened against bot-protection responses
   * (non-OK status or non-`application/json` content-type → publish empty
   * payload via dev-only logger, no `console.error`) so bot/crawler traffic
   * doesn't burn the Sentry quota. See #478 / PR #479.
   *
   */
  async open() {
    this.showDialog();

    // Fetch current cart state and publish custom analytics event for cart drawer viewed
    // Using Shopify.analytics.publish as per documentation:
    // https://shopify.dev/docs/api/web-pixels-api/emitting-data
    // @ts-ignore - Shopify.analytics is available in the theme context
    if (typeof Shopify !== "undefined" && Shopify.analytics) {
      try {
        // Fetch current cart state from Cart API to ensure we have the latest data
        // Note: We fetch here instead of using data-cart attribute because the cart
        // may have been updated via JavaScript after page load (add/remove items)
        // cart_url is locale-prefixed (`/cart` or `/fr/cart`). Do not join
        // root_url + `/cart.js` — default locale root is `/`, which becomes
        // the protocol-relative host `//cart.js`.
        const cartUrl = `${window.Theme?.routes?.cart_url || "/cart"}.js`;
        const response = await fetch(cartUrl, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          cache: "no-store",
        });
        const contentType = (
          response.headers.get("content-type") || ""
        ).toLowerCase();

        if (!response.ok || !contentType.includes("application/json")) {
          // Bot protection / CDN stub — not a real cart failure.
          // Publish the analytics event without cart payload so downstream pixels still fire.
          log(
            `Cart fetch soft-failed: status=${response.status} content-type=${contentType || "unknown"}`,
          );
          // @ts-ignore - Shopify.analytics.publish is available at runtime
          Shopify.analytics.publish("theme:cart_drawer_viewed", {});
        } else {
          const cartData = await response.json();

          // Transform Shopify Cart API format to match analytics event structure
          const cart = {
            id: cartData.token,
            lines: cartData.items.map((item) => ({
              merchandise: {
                id: item.variant_id,
                product: {
                  id: item.product_id,
                  title: item.product_title,
                  vendor: item.vendor,
                  url: `${window.location.origin}${item.url}`,
                },
                title: item.variant_title,
                price: {
                  amount: (item.price / 100).toFixed(2),
                  currencyCode: cartData.currency,
                },
                image: {
                  src: item.image,
                },
                sku: item.sku,
              },
              quantity: item.quantity,
            })),
            cost: {
              totalAmount: {
                amount: (cartData.total_price / 100).toFixed(2),
                currencyCode: cartData.currency,
              },
            },
          };

          // @ts-ignore - Shopify.analytics.publish is available at runtime
          Shopify.analytics.publish("theme:cart_drawer_viewed", { cart });
        }
      } catch (error) {
        // Genuine failure (network reject, JSON parse failure on a 200 OK, etc.).
        console.error("[CartDrawer] Failed to fetch cart data:", error);
        reportError(error);
        // Still publish event without cart data as fallback
        // @ts-ignore
        Shopify.analytics.publish("theme:cart_drawer_viewed", {});
      }
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
